import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, WritableSignal, inject, input, signal, type OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KuiModule } from 'app/key-ui';
import { MatchMediaService, MetricsService } from 'app/services';
import { MediaService } from 'app/services/media/media.service';
import { EMPTY, Subject, catchError, from, switchMap, takeUntil, tap } from 'rxjs';
import { VideoPlayerOptions, VjsPlayerComponent } from '../players/vjs-player/vjs-player.component';
import { KuiSnackbarService } from 'app/key-ui/snackbar/snackbar.service';
import { Router } from '@angular/router';

enum VideoRequestState {
    AWAITING_USER,
    REQUEST_SENT,
    SUCCESS,
    FAILED
}

@Component({
    selector: 'key-livestream',
    imports: [
    CommonModule,
    KuiModule,
    VjsPlayerComponent,
    TranslateModule
],
    templateUrl: './livestream.component.html',
    styleUrl: './livestream.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LivestreamComponent implements OnInit, OnDestroy {

    VideoRequestState = VideoRequestState;

    assetId = input.required<string>();
    camera = input.required<string>();
    name = input<string>();
    duration = input<number>(60);

    error: WritableSignal<string> = signal<string>(null);
    loading: WritableSignal<boolean> = signal(false);
    watchLimitReached: WritableSignal<boolean> = signal(false);
    streamEnd: WritableSignal<boolean> = signal(false);
    
    streamStartTime: WritableSignal<string> = signal(null);
    videoRequestState: WritableSignal<VideoRequestState> = signal(VideoRequestState.AWAITING_USER);

    videoSource: WritableSignal<any> = signal(null);
    videoOptions = signal<VideoPlayerOptions>({
        autoplay: true,
        controls: true,
        html5: {
            vhs: {
                playlistExclusionDuration: 60,  // Allow retries for 60 seconds
                maxPlaylistRetries: 5           // Allow up to 5 playlist retries
            }
        }
    });

    streamRequest = new Subject<{
        asset: string,
        camera: string,
        save: boolean,
        duration: number
    }>();

    videoRequest = new Subject<{
        asset: string,
        date: string,
        camera: string,
        duration: number
    }>();

    private destroyed$ = new Subject<boolean>();

    private router = inject(Router);
    private metrics = inject(MetricsService);

    constructor(
        public matchMedia: MatchMediaService,
        public media: MediaService,
        private i18n: TranslateService,
        private snackbar: KuiSnackbarService,
    ) { }

    ngOnInit(): void {
        this.streamRequest.pipe(
            takeUntil(this.destroyed$),
            tap(_ => {
                this.loading.set(true);
                this.streamEnd.set(false);
                this.watchLimitReached.set(false);
                this.streamStartTime.set(new Date().toISOString());
                this.videoRequestState.set(VideoRequestState.AWAITING_USER);
            }),
            switchMap(req => from(this.media.requestLiveStreamUrl(req.asset, req.camera, req.duration, req.save))),
            catchError((err) => {
                console.error(err);
                if (err.status === 400) {
                    // probably reached the asset limit
                    this.watchLimitReached.set(true);
                    this.error.set(err.message);
                } else {
                    this.error.set(this.i18n.instant('MEDIA.ERRORS.GENERIC'));
                }
                this.loading.set(false);
                return EMPTY;
            })
        ).subscribe(result => {
            if (result.status === 'success') {
                this.error.set(null);
                const hls = result.endpoints.find(x => x.startsWith('https://'));
                if (hls) {
                    this.videoSource.set({ src: hls, type: 'application/x-mpegURL' });
                } else {
                    this.error.set(this.i18n.instant('MEDIA.LIVESTREAM.NO_COMPATIBLE_STREAM'));
                }
            } else {
                this.error.set(result.status);
            }
            this.loading.set(false);
        });

        this.videoRequest.pipe(
            takeUntil(this.destroyed$),
            tap(_ => this.videoRequestState.set(VideoRequestState.REQUEST_SENT)),
            switchMap(req => from(this.media.createVideoEvent(req.asset, req.date, req.duration, 0, [req.camera]))),
            catchError((err) => {
                console.error(err);
                this.videoRequestState.set(VideoRequestState.FAILED);
                return EMPTY;
            })
        ).subscribe(_ => {
            this.snackbar.message(this.i18n.instant('MEDIA.STATUS.DESCRIPTION.QUEUED'), this.i18n.instant('MEDIA.VIDEO_REQUEST.MODAL.NAME'), 'video');
            this.videoRequestState.set(VideoRequestState.SUCCESS);
        })
    }

    ngOnDestroy(): void {
        this.destroyed$.next(true);
    }

    requestStream() {
        this.streamRequest.next({
            asset: this.assetId(),
            camera: this.camera(),
            save: false, // People want the save button showing after a stream has been watched...
            duration: this.duration()
        });

        this.captureAnalytics('start');
    }

    requestVideo() {
        this.videoRequest.next({
            asset: this.assetId(),
            date: this.streamStartTime(),
            camera: this.camera(),
            duration: this.duration()
        });
    }

    onLiveStreamEnd() {
        this.streamEnd.set(true);
        this.captureAnalytics('end');
    }

    onLiveStreamPlay(): void {
        this.captureAnalytics('play');
    }

    onLiveStreamPause(): void {
        this.captureAnalytics('pause');
    }

    onLiveStreamError(_err: any) {
        // An error would stop the stream...
        console.error('errored', _err);
        this.streamEnd.set(true);
    }

    //#region Analytics

    private captureAnalytics(step: 'start' | 'play' | 'pause' | 'end') {
        if (!this.router.url.includes('status')) return;

        this.metrics.capture('livestream_view', {
            screen: 'live_map',
            source: 'main_page',
            step: step
        });
    }

}
